import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, BookCheck, Bot, Brain, Crosshair, Eye, Flame, Pencil, Sparkles, TrendingUp, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const BADGES = {
  ai: { group: "quality", label: "AI", desc: "Yapay zeka tarafından oluşturulmuş, kontrol edilmiş içerik.", icon: Bot, color: "#A78BFA" },
  expert: { group: "quality", label: "Uzman Onaylı", desc: "Gerçek bir kimya öğretmeni veya akademisyen tarafından incelenip onaylanmış, yüksek kaliteli sorular.", icon: BadgeCheck, color: "#38BDF8" },
  curriculum: { group: "quality", label: "Müfredat Uyumlu", desc: "Güncel MEB kazanımlarına veya üniversite ders izlencesine %100 uygun olduğu doğrulanmış içerik.", icon: BookCheck, color: "#22C55E" },
  basic: { group: "level", label: "Isınma Turu", desc: "Kavramları öğretmeye yönelik başlangıç seviyesi sorular.", icon: Flame, color: "#FDBA74" },
  advanced: { group: "level", label: "Beyin Yakan", desc: "Olimpiyat seviyesinde veya çok adımlı düşünme gerektiren zorlayıcı sorular.", icon: Brain, color: "#F472B6" },
  trap: { group: "level", label: "Hata Avcısı", desc: "Çeldiricileri çok güçlü, öğrencilerin en çok hata yaptığı sorular (ters köşe).", icon: Crosshair, color: "#F87171" },
  popular: { group: "stat", label: "Popüler", desc: "En çok görüntülenen ve oynatılan içerikler arasında (ilk %10).", icon: TrendingUp, color: "#FACC15" },
  new: { group: "stat", label: "Yeni", desc: "Son 1 hafta içinde eklenmiş yepyeni içerik.", icon: Sparkles, color: "#2DD4BF" },
  success: { group: "stat", label: "Yüksek Başarı", desc: "En az 20 yanıtta ortalama %75 ve üzeri doğru; pekiştirici içerik.", icon: Trophy, color: "#4ADE80" },
};
const GROUPS = [["quality", "Kalite ve doğrulama", "quality"], ["level", "Zorluk ve seviye", "level_badges"], ["stat", "Etkileşim ve istatistik", "stat_badges"]];

export const trackView = (kind, id) => { if (id) api.post(`/views/${kind}/${id}`).catch(() => {}); };

const Chip = ({ k, testId }) => {
  const b = BADGES[k];
  const Icon = b.icon;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 border cursor-default" style={{ color: b.color, borderColor: `${b.color}66`, background: `${b.color}14` }} data-testid={testId}>
            <Icon className="h-3.5 w-3.5" />{b.label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{b.desc}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const useRights = (item) => {
  const { teacher } = useAuth();
  const dev = Boolean(teacher?.developer);
  const admin = teacher?.role === "admin";
  return { quality: dev || admin || Boolean(teacher?.academic), level: dev || admin || item.owned, stat: dev };
};

const OVR = [["auto", "Otomatik"], ["on", "Her zaman göster"], ["off", "Gizle"]];

const ReviewBox = ({ kind, item, onDone }) => {
  const [note, setNote] = useState("");
  const review = async (approve) => {
    try { await api.post(`/badges/${kind}/${item.id}/review`, { approve, note }); toast.success(approve ? "Onaylandı, not öğretmene gönderildi" : "Reddedildi, not öğretmene gönderildi"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border border-sky-400/40 bg-sky-500/10 p-3 space-y-2" data-testid="badge-review">
      <p className="text-sm font-semibold text-sky-200">Bu içerik için Uzman Onaylı talebi var</p>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="Öğretmene kısa bir not (isteğe bağlı)" data-testid="badge-review-note" />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => review(true)} data-testid="badge-review-approve"><BadgeCheck className="h-4 w-4 mr-1" />Onayla</Button>
        <Button size="sm" variant="outline" className="text-red-300" onClick={() => review(false)} data-testid="badge-review-reject">Reddet</Button>
      </div>
    </div>
  );
};

const BadgeDialog = ({ kind, item, open, onOpenChange, onSaved }) => {
  const rights = useRights(item);
  const [q, setQ] = useState(item.quality || []);
  const [l, setL] = useState(item.level_badges || []);
  const [o, setO] = useState(item.stat_overrides || {});
  const toggle = (list, set, k) => set(list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);
  const save = async () => {
    const body = {};
    if (rights.quality) body.quality = q;
    if (rights.level) body.level_badges = l;
    if (rights.stat) body.stat_overrides = { popular: o.popular ?? null, new: o.new ?? null, success: o.success ?? null };
    try { await api.put(`/badges/${kind}/${item.id}`, body); toast.success("Rozetler kaydedildi"); onSaved?.(); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); }
  };
  const box = (k, list, set, enabled) => (
    <label key={k} className={`flex items-start gap-2 text-sm ${enabled ? "cursor-pointer" : "opacity-50"}`}>
      <Checkbox checked={list.includes(k)} disabled={!enabled} onCheckedChange={() => toggle(list, set, k)} data-testid={`badge-check-${k}`} />
      <span><b style={{ color: BADGES[k].color }}>{BADGES[k].label}</b> <span className="text-slate-400">{BADGES[k].desc}</span></span>
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="badge-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Rozetler</DialogTitle></DialogHeader>
        {item.expert_requested && rights.quality && <ReviewBox kind={kind} item={item} onDone={() => { onSaved?.(); onOpenChange(false); }} />}
        <p className="eyebrow">Kalite ve doğrulama · akademisyen, yönetici, geliştirici</p>
        {Object.keys(BADGES).filter((k) => BADGES[k].group === "quality").map((k) => box(k, q, setQ, rights.quality))}
        <p className="eyebrow pt-2">Zorluk ve seviye · içeriğin sahibi (yönetici ve geliştirici denetler)</p>
        {Object.keys(BADGES).filter((k) => BADGES[k].group === "level").map((k) => box(k, l, setL, rights.level))}
        <p className="eyebrow pt-2">Etkileşim ve istatistik · sistem otomatik verir{rights.stat ? ", geliştirici değiştirebilir" : ""}</p>
        {Object.keys(BADGES).filter((k) => BADGES[k].group === "stat").map((k) => (
          <div key={k} className="flex items-center gap-3 text-sm">
            <span className="flex-1"><b style={{ color: BADGES[k].color }}>{BADGES[k].label}</b> <span className="text-slate-400">{BADGES[k].desc}</span></span>
            <Select disabled={!rights.stat} value={o[k] == null ? "auto" : o[k] ? "on" : "off"} onValueChange={(v) => setO({ ...o, [k]: v === "auto" ? null : v === "on" })}>
              <SelectTrigger className="h-9 w-40" data-testid={`badge-stat-${k}`}><SelectValue /></SelectTrigger>
              <SelectContent>{OVR.map(([v, t]) => <SelectItem key={v} value={v}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        <DialogFooter><Button onClick={save} className="font-bold" data-testid="badge-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ContentBadges = ({ kind, item, onChange, compact = false, max = 3 }) => {
  const rights = useRights(item);
  const [open, setOpen] = useState(false);
  const all = [...(item.quality || []), ...(item.level_badges || []), ...(item.stat_badges || [])].filter((k) => BADGES[k]);
  const keys = compact ? all.slice(0, max) : all;
  const rest = all.slice(keys.length);
  const canEdit = rights.quality || rights.level || rights.stat;
  const canRequest = item.owned && !rights.quality && !(item.quality || []).includes("expert");
  const request = async () => {
    try { await api.post(`/badges/${kind}/${item.id}/request`); toast.success("Onay talebi akademisyenlere gönderildi"); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()} data-testid={`badges-${item.id}`}>
      <span className="inline-flex items-center gap-1 text-xs text-slate-400" title="Görüntülenme" data-testid={`views-${item.id}`}><Eye className="h-3.5 w-3.5" />{item.views || 0}</span>
      {keys.map((k) => <Chip key={k} k={k} testId={`badge-${k}-${item.id}`} />)}
      {rest.length > 0 && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild><span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-white/10 text-slate-200 cursor-default" data-testid={`badges-more-${item.id}`}>+{rest.length}</span></TooltipTrigger>
            <TooltipContent>{rest.map((k) => BADGES[k].label).join(" · ")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {canEdit && (
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs text-slate-400" title="Rozetleri düzenle" onClick={() => setOpen(true)} data-testid={`badges-edit-${item.id}`}><Pencil className="h-3 w-3" />{!compact && <span className="ml-1">Rozet</span>}</Button>
      )}
      {canRequest && !compact && (
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs text-sky-300" disabled={item.expert_requested} onClick={request} data-testid={`badges-request-${item.id}`}>
          <BadgeCheck className="h-3 w-3 mr-1" />{item.expert_requested ? "Onay bekleniyor" : "Uzman onayı iste"}
        </Button>
      )}
      {open && <BadgeDialog kind={kind} item={item} open={open} onOpenChange={setOpen} onSaved={onChange} />}
    </span>
  );
};

export const itemBadges = (i) => [...(i.quality || []), ...(i.level_badges || []), ...(i.stat_badges || [])];
